import type { ColorMode } from "@/components/layouts/ColorModeContext";
import { RemoteJobSchema } from "@/features/remote_jobs";
import { AbcdeRiskValue } from "@/features/risk_bands";
import { AnaHazardId } from "@/store/services/facades/types";
import { HaloRequest } from "@/store/services/facades/types/halo";
import { SimpleStructuralRequest } from "@/store/services/facades/types/simple";
import { ProcessStatus } from "@/types/processes";
import { translucent } from "@/utils/color";
import { AnomalyManagamentSupportedHazards, DefenceSupportedHazards } from "@/utils/hazards";
import { Feature, Polygon } from "@turf/turf";
import type { CSSProperties } from "react";
export interface PortfolioExportJobSchemaNote {
  detail: string;
  title: string;
}
export interface LargeSiteScreenRiskiestPoint {
  hazard: AnaHazardId | "total";
  year: 2025 | 2050 | 2100;
}
export type AutoAdaptationStatus = "pending" | "completed" | null;

/**
 * Variant of RemoteJobSchema with extra information for Portfolio Export runs.
 */
export interface PortfolioExportJobSchema extends RemoteJobSchema {
  /** Unique ID of the run */
  id: string;
  /** Various metrics concerning assets. */
  assets: {
    /** Number of erroneous assets. */
    errorCount: number;
    /** Number of successfully processed assets. */
    processedCount: number;
    /** Number of assets in this job. */
    totalCount: number;
    /** Number of assets with is_processed=false in this job. */
    unprocessedCount: number;
  };
  /**
   * Status of schema adaptations, only populated after schema assets are processed
   */
  adaptations: {
    /** Number of successfully processed adaptation actions. */
    processedCount: number;
    /** Number of errored adaptation actions. */
    errorCount: number;
    /** Number of total adaptation actions. */
    totalCount: number;
    /** Number of pending adaptation actions. */
    unprocessedCount: number;
  } | undefined;
  /** Status of the schema */
  status: ProcessStatus;
  /** ID of Portfolio Export Summary */
  summaryId: string;
  isSummaryGenerated: boolean | null;
  userId: string;
  email: string;
  rerunEmail: string | null;
  rerunAt: string | null;
  groupId: string;
  groupName: string;
  secret: {
    secretKey: CryptoKey;
    salt: ArrayBuffer;
    secretAndSalt: string;
  };
  isLargeSite: boolean;
  isPrivate: boolean;
  stamps: string[];
  metadata: {
    polygons: Feature<Polygon>[];
    riskiestPoint: LargeSiteScreenRiskiestPoint;
  } | undefined;
  incorporatedHazards: AnaHazardId[];
  regionType: PortfolioRegionType | undefined;
  autoAdaptationStatus: AutoAdaptationStatus;
  collectionVersion: number | null;
  updateAvailable: boolean;
}
export interface PortfolioExportResultLine {
  status: "success";
  id: string;
  geocodeQuality: string;
  scenario: string;
  year: number;
  riskBand: AbcdeRiskValue;
  hazards: string;
  location?: {
    latitude: number;
    longitude: number;
  } | null | undefined;
}
export interface PortfolioExportResultError {
  status: "error";
  id: string;
  title: string;
  detail: string;
}
export interface PortfolioExportSummaryRequest {
  assets: {
    uuid: string;
    mv: number;
    rc: number;
    mr: number;
  }[];
  settings?: {
    currency_code?: string;
  };
  stamps?: string[];
  report_id?: string;
}
export type PortfolioExportAssetData = (HaloRequest | SimpleStructuralRequest) & {
  item_id?: string;
  coordinates?: {
    latitude: number;
    longitude?: number;
    elevation?: number;
  };
  geocoding?: {
    address?: string;
  };
  userSuppliedId?: string | number;
  hk?: {
    tpu: string;
    subunit: string;
  };
  defences?: DefenceSupportedHazards;
  anomaly_management_rp?: AnomalyManagamentSupportedHazards;
  financial?: {
    market_value: number;
    interest_rate: number;
    replacement_cost: number;
  };
};
export type PortfolioRegionType = "hk" | "global";
export const portfolioTypeColours: Record<PortfolioRegionType, string> = {
  global: "#36887E",
  hk: "#EE8D1C"
};

/**
 * Label tones. The hue identifies the portfolio type, so only lightness moves:
 * darker than the base for text on a pale tint, lighter for text on a deep one.
 * The base colours above are too light to serve as text in light mode — the
 * "hk" orange reads 2.18:1 on its own tint — which is the same problem antd's
 * preset tags solve with a separate `${color}Text` shade.
 */
export const portfolioTypeLabelColours: Record<"light" | "dark", Record<PortfolioRegionType, string>> = {
  light: {
    global: "#2c746b",
    hk: "#a35c08"
  },
  dark: {
    global: "#46978d",
    hk: "#de8c2c"
  }
};

/**
 * Tinted rather than a solid fill, matching the antd preset tags it sits beside
 * (PortfolioExportJobStatus uses color="blue"/"orange"). A solid saturated block
 * reads as light-mode chrome on a dark page, and antd forces white text on any
 * custom Tag colour, which was 2.48:1 on the "hk" orange.
 *
 * The tint composites over whatever surface is behind it, so only the
 * foreground needs a per-mode value.
 */
export const portfolioTagStyle = (type: PortfolioRegionType, colorMode: ColorMode): CSSProperties => {
  const base = portfolioTypeColours[type];
  return {
    // The tint composites over whatever surface is behind it, so it needs no
    // per-mode value; only the label does.
    background: translucent(base, 0.15),
    borderColor: translucent(base, 0.4),
    color: portfolioTypeLabelColours[colorMode][type]
  };
};
export type PortfolioNewRunModalStatus = "INITIAL" | "TYPE_SELECTION" | "CUSTOMISING" | "IN_REVIEW" | "UPLOADING";